import type { computed, Ref, ComputedRef } from 'vue';

/**
 * Interface for a required field in a form.
 * @property value - The value of the field, can be any type.
 * @property name - The name of the field, used for identification.
 * @property validator - Optional custom validation function that takes the field value and returns a boolean indicating if the field is valid.
 */
interface RequiredField {
  value: any;
  name: string;
  validator?: (value: any) => boolean;
}

/**
 * Composable for determining if all required fields in a form are filled out.
 * @param fields - An array of required fields, each with a value, name, and optional custom validator.
 * This can be a Ref, ComputedRef, or a function that returns an array of RequiredField objects.
 */
export function useFormValidation(fields: Ref<RequiredField[]> | ComputedRef<RequiredField[]> | (() => RequiredField[])) {
  /**
   * Computed Property to check if all required fields are filled.
   */
  const areRequiredFieldsFilled = computed(() => {
    const currentFields = typeof fields === 'function' ? fields() : fields.value;

    return currentFields.every(field => {
      // If a custom validator is provided, use it to validate the field
      if (field.validator) {
        return field.validator(field.value);
      }

      // Standard validation for arrays
      if (Array.isArray(field.value)) {
        return field.value.length > 0;
      }

      // Check for null, undefined, or empty string
      return field.value !== null && field.value !== undefined && field.value !== '';
    });
  });

  /**
   * Computed Property for determining if the submit button should be disabled.
   */
  const isSubmitDisabled = computed(() => {
    return !areRequiredFieldsFilled.value;
  });

  /**
   * Computed Property to get the names of missing required fields.
   */
  const getMissingRequiredFields = computed(() => {
    const currentFields = typeof fields === 'function' ? fields() : fields.value;

    return currentFields
      .filter(field => {
        if (field.validator) {
          return !field.validator(field.value);
        }

        if (Array.isArray(field.value)) {
          return field.value.length === 0;
        }

        return field.value === null || field.value === undefined || field.value === '';
      })
      .map(field => field.name);
  });

  return {
    areRequiredFieldsFilled,
    isSubmitDisabled,
    getMissingRequiredFields
  };
}
